/* eslint-disable max-len */
import { Formik, useFormikContext } from 'formik';
import { ChangeEvent, ReactElement, useEffect, useMemo } from 'react';
import { useAppSelector } from '../../../redux/hooks';
import {
  selectLiteracyScreeningDiagnostics,
  selectLiteracyScreeningOptionSets,
  selectLiteracyScreeningSchoolList,
  selectLiteracyScreeningUpdate,
} from '../../../redux/slices/literacyScreeningSlice';
import {
  LiteracyScreeningDiagnosticMeasuresModel,
  LITERACY_SCREENING_COMMON_OPTION_SETS,
} from '../../../types/LiteracyScreeningDiagnosticMeasuresModel';
import { PanelType } from '../../../types/PanelType';
import { FormRadioListOrientation } from '../../../types/propTypes/FormRadioListPropTypes';
import { SelectOption } from '../../../types/SelectOption';
import {
  LiteracyScreeningReimbursementFormValues,
  LiteracyScreeningReimbursementValidationSchema,
} from '../../../types/Validation/LiteracyScreeningReimbursementValidationSchema';
import { isSPO } from '../../../utilities/userUtilities';
import FormRadioList from '../../FormInputs/FormRadioList/FormRadioList';
import FormTextbox from '../../FormInputs/FormTextbox/FormTextbox';
import Panel from '../../Panel/Panel';
import { isLiteracyScreeningDiagnosticsPanelEnabled } from './LiteracyScreeningDiagnosticMeasuresPanel';

export const LITERACY_SCREENING_EXPLANATION_PLACEHOLDER =
  'Include an explanation why the district is not requesting reimbursement.';

export interface LiteracyScreeningReimbursementEditState {
  dirty: boolean;
  isValid: boolean;
  values: LiteracyScreeningReimbursementFormValues;
}

interface LiteracyScreeningReimbursementPanelProps {
  onEditStateChange?: (state: LiteracyScreeningReimbursementEditState) => void;
}

const ReimbursementStateReporter = ({
  disabled,
  onEditStateChange,
}: {
  disabled: boolean;
  onEditStateChange?: (state: LiteracyScreeningReimbursementEditState) => void;
}): null => {
  const { values, dirty } =
    useFormikContext<LiteracyScreeningReimbursementFormValues>();

  useEffect(() => {
    onEditStateChange?.({
      dirty,
      isValid:
        disabled ||
        LiteracyScreeningReimbursementValidationSchema.isValidSync(values),
      values,
    });
  }, [disabled, dirty, onEditStateChange, values]);

  return null;
};

const toFormValues = (
  diagnostics: LiteracyScreeningDiagnosticMeasuresModel | null
): LiteracyScreeningReimbursementFormValues => ({
  reimbursement: diagnostics?.reimbursement || '',
  explanation: diagnostics?.explanation || '',
});

const getEnabledOptions = (
  optionSets: Record<string, SelectOption[]>,
  key: string
): SelectOption[] =>
  (optionSets[key] || []).filter((option) => !option.isDisabled);

const LiteracyScreeningReimbursementFields = ({
  yesNoOptions,
  disabled,
}: {
  yesNoOptions: SelectOption[];
  disabled: boolean;
}): ReactElement => {
  const { values, setFieldValue } =
    useFormikContext<LiteracyScreeningReimbursementFormValues>();
  const isExplanationRequired = !disabled && values.reimbursement === 'No';

  const handleReimbursementClick = (
    event: ChangeEvent<HTMLButtonElement>
  ): void => {
    if (event.target.value !== 'No') {
      setFieldValue('explanation', '');
    }
  };

  return (
    <div className="field-row field-row-grid-1-1">
      <FormRadioList
        displayName="Is the LEA requesting reimbursement?"
        field="reimbursement"
        options={yesNoOptions}
        optionOrientation={FormRadioListOrientation.Horizontal}
        validationSchema={LiteracyScreeningReimbursementValidationSchema}
        disabled={disabled}
        onClick={handleReimbursementClick}
      />
      <FormTextbox
        displayName="Explanation"
        field="explanation"
        disabled={!isExplanationRequired}
        placeholder={LITERACY_SCREENING_EXPLANATION_PLACEHOLDER}
        validationSchema={LiteracyScreeningReimbursementValidationSchema}
        classConfig={{
          labelClasses: isExplanationRequired ? 'forced-required-label' : '',
        }}
      />
    </div>
  );
};

const LiteracyScreeningReimbursementPanel = ({
  onEditStateChange,
}: LiteracyScreeningReimbursementPanelProps): ReactElement => {
  const diagnostics = useAppSelector(selectLiteracyScreeningDiagnostics);
  const optionSets = useAppSelector(selectLiteracyScreeningOptionSets);
  const schoolList = useAppSelector(selectLiteracyScreeningSchoolList);
  const literacyScreeningUpdate = useAppSelector(selectLiteracyScreeningUpdate);
  const isEnabled =
    isLiteracyScreeningDiagnosticsPanelEnabled(schoolList) &&
    literacyScreeningUpdate &&
    !isSPO();
  const initialValues = useMemo(() => toFormValues(diagnostics), [diagnostics]);
  const yesNoOptions = getEnabledOptions(
    optionSets,
    LITERACY_SCREENING_COMMON_OPTION_SETS.yesNo
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={LiteracyScreeningReimbursementValidationSchema}
      onSubmit={() => undefined}
    >
      <>
        <ReimbursementStateReporter
          disabled={!isEnabled}
          onEditStateChange={onEditStateChange}
        />
        <Panel
          panelType={PanelType.INFO}
          heading="Reimbursement"
          className="literacy-screening-reimbursement-panel"
        >
          <LiteracyScreeningReimbursementFields
            yesNoOptions={yesNoOptions}
            disabled={!isEnabled}
          />
        </Panel>
      </>
    </Formik>
  );
};

export default LiteracyScreeningReimbursementPanel;
